/**
 * Preview Authentication Utility
 * Handles preview ID protection for preview routes when no Storyblok token is present
 * Uses HTTP Basic Auth with server-side validation
 */

const PREVIEW_AUTH_KEY = 'preview_authenticated';

/**
 * Check if Storyblok preview token exists in URL
 * @returns {boolean} True if preview token is present
 */
export function hasPreviewToken() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return false;
    }

    const params = new URLSearchParams(window.location.search);

    // Check for Storyblok token in bracket notation: _storyblok_tk[token]
    const bracketToken = params.get('_storyblok_tk[token]');
    if (bracketToken && bracketToken.length > 0) {
      return true;
    }

    // Check for standard Storyblok parameters
    const hasStoryblokParams =
      params.has('_storyblok_tk') ||
      params.has('_storyblok_preview_tk') ||
      params.has('preview_token') ||
      params.has('previewToken') ||
      params.has('_storyblok');

    return hasStoryblokParams;
  } catch (error) {
    console.warn('[Preview Auth] Error checking preview token:', error);
    return false;
  }
}

/**
 * Get Storyblok preview token from URL
 * Handles bracket notation like _storyblok_tk[token]=value
 * @returns {string|null} Preview token or null
 */
export function getPreviewToken() {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }

    const search = window.location.search;
    if (!search) {
      return null;
    }

    // Parse URL manually to handle bracket notation properly
    // URLSearchParams might not handle brackets correctly in all browsers
    const params = new URLSearchParams(search);

    // Try bracket notation first: _storyblok_tk[token]
    // Check all keys for bracket notation pattern
    for (const key of params.keys()) {
      if (
        key === '_storyblok_tk[token]' ||
        (key.includes('_storyblok_tk[') && key.includes('token]'))
      ) {
        const token = params.get(key);
        if (token && token.trim().length > 0) {
          console.log('[Preview Auth] Found token in bracket notation:', key);
          return token;
        }
      }
    }

    // Also try direct bracket notation (some browsers parse it differently)
    const bracketMatch = search.match(/[?&]_storyblok_tk\[token\]=([^&]+)/);
    if (bracketMatch && bracketMatch[1]) {
      const token = decodeURIComponent(bracketMatch[1]);
      if (token && token.trim().length > 0) {
        console.log('[Preview Auth] Found token via regex match');
        return token;
      }
    }

    // Try standard parameters
    const standardToken =
      params.get('_storyblok_tk') ||
      params.get('_storyblok_preview_tk') ||
      params.get('preview_token') ||
      params.get('previewToken');

    if (standardToken && standardToken.trim().length > 0) {
      console.log('[Preview Auth] Found token in standard parameter');
      return standardToken;
    }

    return null;
  } catch (error) {
    console.warn('[Preview Auth] Error getting preview token:', error);
    return null;
  }
}

/**
 * Create HTTP Basic Auth header value
 * @param {string} username - Username (can be empty string)
 * @param {string} previewId - Preview ID
 * @returns {string} Authorization header value (e.g., "Basic <base64>")
 */
export function createBasicAuthHeader(username, previewId) {
  const credentials = `${username || ''}:${previewId}`;
  const encoded = btoa(credentials);
  return `Basic ${encoded}`;
}

/**
 * Validate preview authentication via backend API
 * @param {string} username - Username (optional, defaults to empty string)
 * @param {string} previewId - Preview ID
 * @returns {Promise<boolean>} True if authentication successful
 */
export async function validatePreviewAuth(username = '', previewId) {
  if (!previewId) {
    console.warn('[Preview Auth] No preview ID provided');
    return false;
  }

  try {
    const { getApiUrl } = await import('@/utils/platform-check');
    const apiUrl = getApiUrl();
    const authHeader = createBasicAuthHeader(username, previewId);

    const response = await fetch(`${apiUrl}/api/preview/auth`, {
      method: 'POST',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.success === true;
    }

    return false;
  } catch (error) {
    console.error('[Preview Auth] Error validating credentials:', error);
    return false;
  }
}

/**
 * Check if user is authenticated for preview (has token, valid password session, or valid ID in route)
 * @returns {boolean} True if authenticated
 */
export function isPreviewAuthenticated() {
  // If preview token exists, always allow access
  if (hasPreviewToken()) {
    return true;
  }

  // Check if route contains valid preview ID
  try {
    if (typeof window !== 'undefined' && window.location) {
      const pathname = window.location.pathname;
      const expectedId = process.env.VUE_APP_PREVIEW_ID;

      if (expectedId && pathname.startsWith('/preview/')) {
        // Extract ID from pathname: /preview/:id/...
        const match = pathname.match(/^\/preview\/([^/]+)/);
        if (match && match[1] === expectedId) {
          // ID in URL matches expected ID, automatically authenticate
          setPreviewAuthenticated(true);
          return true;
        }
      }
    }
  } catch (error) {
    console.warn('[Preview Auth] Error checking route ID:', error);
  }

  // Check session storage for preview ID authentication
  try {
    if (typeof sessionStorage !== 'undefined') {
      return sessionStorage.getItem(PREVIEW_AUTH_KEY) === 'true';
    }
  } catch (error) {
    console.warn('[Preview Auth] Error checking session storage:', error);
  }

  return false;
}

/**
 * Set preview authentication status
 * @param {boolean} authenticated - Authentication status
 */
export function setPreviewAuthenticated(authenticated) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (authenticated) {
        sessionStorage.setItem(PREVIEW_AUTH_KEY, 'true');
      } else {
        sessionStorage.removeItem(PREVIEW_AUTH_KEY);
      }
    }
  } catch (error) {
    console.warn('[Preview Auth] Error setting authentication:', error);
  }
}

/**
 * Clear preview authentication
 */
export function clearPreviewAuth() {
  setPreviewAuthenticated(false);
}
